"use client";

import type { useChat } from "@ai-sdk/react";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { useParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  URLGroupPatternType,
  URLGroupPatternTypeLabels,
} from "@/lib/ai/tools/shared/url-group-pattern-type";
import type { AgentUIMessage } from "@/lib/ai/types";
import { OrderList } from "../shared/order-list";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { UPDATE_URL_GROUP_ORDER_NAME } from "./constants";
import type { UpdateUrlGroupOrderInput } from "./schema";

const UrlGroupsQuery = graphql(`
  query UrlGroupOrderConfirm($id: ID!) {
    node(id: $id) {
      __typename
      ... on SearchAnalyticsProject {
        id
        name
        searchAnalytics {
          id
          urlGroups {
           id
           name
            description
            patterns {
              type
              pattern
            }
          }
        }
      }
      ... on UserAnalyticsProject {
        id
        name
        userAnalytics {
          id
          urlGroups {
            id
            name
            description
            patterns {
              type
              pattern
            }
          }
        }
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function UpdateUrlGroupOrderConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { i18n } = useLingui();
  const params = useParams();
  const lang = typeof params.lang === "string" ? params.lang : i18n.locale;
  const workspaceId =
    typeof params.workspaceId === "string"
      ? decodeURIComponent(params.workspaceId)
      : "";
  const isTarget =
    isToolUIPart(part) && getToolName(part) === UPDATE_URL_GROUP_ORDER_NAME;
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<UpdateUrlGroupOrderInput>;
  const targetType = input.target;
  const initialIds = input.ids ?? [];
  const projectId = input.projectId ?? undefined;
  // projectId さえあればターゲット種別を自動判定し、クエリを必ず実行する
  const [{ data, fetching }] = useQuery({
    query: UrlGroupsQuery,
    variables: {
      id: projectId ?? "",
    },
    requestPolicy: "network-only",
    pause: !projectId,
  });

  const node = data?.node;
  const inferredTarget =
    node?.__typename === "UserAnalyticsProject"
      ? "ua"
      : node?.__typename === "SearchAnalyticsProject"
        ? "sa"
        : undefined;
  const effectiveTarget = inferredTarget ?? targetType ?? "sa";

  const currentGroups = (() => {
    switch (node?.__typename) {
      case "SearchAnalyticsProject":
        return node.searchAnalytics?.urlGroups ?? [];
      case "UserAnalyticsProject":
        return node.userAnalytics?.urlGroups ?? [];
      default:
        return [];
    }
  })();

  const currentMap = new Map(currentGroups.map((g) => [g?.id ?? "", g?.name]));
  const detailMap = useMemo(() => {
    const map = new Map<
      string,
      {
        description?: string | null;
        patterns?: { type?: string | null; pattern?: string | null }[];
      }
    >();
    currentGroups.forEach((g) => {
      if (g?.id) {
        map.set(g.id, {
          description: g.description,
          patterns: (g.patterns ?? []).map((p) => ({
            type: p?.type ?? null,
            pattern: p?.pattern ?? null,
          })),
        });
      }
    });
    return map;
  }, [currentGroups]);
  const currentOrder = currentGroups
    .map((g) => g?.id)
    .filter((id): id is string => Boolean(id));
  const projectNode = (() => {
    switch (node?.__typename) {
      case "SearchAnalyticsProject":
        return node;
      case "UserAnalyticsProject":
        return node;
      default:
        return undefined;
    }
  })();

  const mergedInitialIds = useMemo(
    () => Array.from(new Set([...(initialIds ?? [])])),
    [initialIds],
  );

  const [orderIds, setOrderIds] = useState<string[]>(mergedInitialIds);

  useEffect(() => {
    // 初回や現在の順序が未設定の場合に、取得した現行順序を同期
    if (!orderIds.length && currentOrder.length) {
      setOrderIds(currentOrder);
    }
  }, [currentOrder, orderIds.length]);

  const displayOrder =
    orderIds.length > 0 ? Array.from(new Set(orderIds)) : currentOrder;

  const shouldRender = isTarget && isInputAvailable;

  if (!shouldRender) return null;

  return (
    <div className="p-4 text-sm space-y-3">
      <OrderList
        title={
          <span className="text-sm md:text-base">
            <Trans>
              Do you want to update this{" "}
              {targetType === "ua" ? "User Analytics" : "Search Analytics"} URL
              group order?
            </Trans>
          </span>
        }
        badges={
          <>
            <Badge variant="outline" className="mr-2">
              <Trans>URL Groups</Trans>
            </Badge>
            {fetching ? (
              <Skeleton className="inline-block h-3 w-40 align-middle" />
            ) : projectNode ? (
              <a
                className="text-primary underline"
                target="_blank"
                rel="noreferrer noopener"
                href={
                  workspaceId
                    ? `/${encodeURIComponent(lang)}/${encodeURIComponent(
                        workspaceId,
                      )}/${effectiveTarget === "ua" ? "ua" : "sa"}/${encodeURIComponent(
                        projectNode.id,
                      )}/settings`
                    : "#"
                }
              >
                {projectNode.name}
              </a>
            ) : (
              <span className="font-medium text-muted-foreground">
                <Trans>Project name unavailable</Trans>
              </span>
            )}
          </>
        }
        items={Array.from(currentMap.entries()).map(([id, label]) => ({
          id,
          label: label ?? <Trans>Unknown</Trans>,
          detail: (
            <div className="space-y-1">
              {detailMap.get(id)?.description ? (
                <div className="text-muted-foreground leading-snug whitespace-pre-wrap">
                  {detailMap.get(id)?.description}
                </div>
              ) : null}
              {detailMap.get(id)?.patterns?.length ? (
                <ul className="list-disc pl-4 space-y-0.5 text-foreground">
                  {detailMap
                    .get(id)
                    ?.patterns?.filter((p) => p?.pattern)
                    .map((p, i) => {
                      const label =
                        (p?.type as URLGroupPatternType | undefined) &&
                        URLGroupPatternTypeLabels[p.type as URLGroupPatternType]
                          ? i18n._(
                              URLGroupPatternTypeLabels[
                                p.type as URLGroupPatternType
                              ],
                            )
                          : (p?.type ?? "");
                      return (
                        <li
                          key={`${id}-p-${i}`}
                          className="text-muted-foreground"
                        >
                          <span className="font-medium">{label}</span>
                          {": "}
                          <span className="break-all text-foreground">
                            {p?.pattern}
                          </span>
                        </li>
                      );
                    })}
                </ul>
              ) : null}
            </div>
          ),
        }))}
        order={displayOrder}
        loading={fetching}
        fallbackLabel={i18n._("No new order was provided")}
        onChange={setOrderIds}
        enableDetailTooltip
        maxHeight="24rem"
      />

      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={UPDATE_URL_GROUP_ORDER_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
          overrideInput={{ ...input, ids: displayOrder }}
        >
          <Trans>Update</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={UPDATE_URL_GROUP_ORDER_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
